import { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { apiCollections } from "services";
import { useDispatch } from "react-redux";
import { CollectionFeed } from "components/feed/CollectionFeed";
import { CollectionHeader } from "components/collections";
import { PopOverMenu } from "components/UI/PopOverMenu";
import { EditCollectionPopup, DeleteCollectionPopup } from "components/UI/Popups/Collections";
import { useCollectionQuery } from "queries";
import { useUserName } from "hooks";
import { ICollection } from "services/types";
import { routes } from "routes";
import { getCollectionMenu } from "./constants";
import { setServerResponse } from "store/application/application-action";

import "../common.scss";
import "./CollectionItemsPage.scss";
import { FooterAds } from "components/ads/FooterAds";
import { ShareCollectionMenu } from "components/UI/ShareMenu/ShareCollectionMenu";

const requireString = (value: string | undefined): string => {
  if (value === undefined) {
    throw new Error("required parameter not set");
  }

  return value;
};

export const CollectionItemsPage = () => {
  const params = useParams();
  const userName = requireString(params.userId);
  const collectionId = requireString(params.id);
  const dispatch = useDispatch();

  const currentUser = useUserName();
  const isEditable = currentUser === userName;
  const navigate = useNavigate();

  const [isMenuVisible, setMenuVisible] = useState<boolean>(false);
  const [isEditVisible, setEditVisible] = useState<boolean>(false);
  const [isDeleteCollectionVisible, setDeleteCollectionVisible] = useState<boolean>(false);
  const [isDeleteGifsVisible, setDeleteGifsVisible] = useState<boolean>(false);
  const [isShareVisible, setShareVisible] = useState<boolean>(false);
  const [isManage, setManage] = useState<boolean>(false);
  const [menuRef, setMenuRef] = useState<HTMLElement|null>(null);
  const [selection, setSelection] = useState<string[]>([]);
  const [collection, setCollection] = useState<ICollection|undefined>(undefined);

  const query = useCollectionQuery({
    userName,
    collectionId,
    showPrivate: isEditable,
  });

  useEffect(() => {
    if (query.status === "success" && query.data) {
      setCollection(query.data);
    }
  }, [query.status, query.data]);

  const handleMore = () => {
    setMenuRef(document.getElementById("collectionMoreButton"));
    setMenuVisible(true);
  };

  const onClose = () => {
    setMenuVisible(false);
    setEditVisible(false);
    setDeleteCollectionVisible(false);
    setShareVisible(false);
  };

  const onShare = () => {
    onClose();
    setShareVisible(true);
  };

  const onEdit = () => {
    onClose();
    setEditVisible(true);
  };

  const onManage = () => {
    onClose();
    setManage(true);
  };

  const onDeleteGifs = () => {
    if (!selection.length) {
      dispatch(setServerResponse("Select GIFs you want to delete"));
      return;
    }

    setDeleteGifsVisible(true);
  };

  const onDeleteCollection = () => {
    onClose();
    setDeleteCollectionVisible(true);
  };

  const onDeleteCollectionConfirm = () => {
    onClose();

    apiCollections.deleteCollection({
      collectionId,
    }).then(() => {
      navigate(routes.userCollections(userName));
    }).catch(() => {
      console.error("Could not delete collection.");
    });
  };

  const onTrash = () => {
    onClose();

    apiCollections.deleteCollectionItems({
      collectionId: collectionId,
      gifsIds: selection,
    }).then(() => {
      setManage(false);
      setSelection([]);
      window.location.reload();
    }).catch(() => {
      console.warn("Error deleting connection items.", selection);
    });
  };

  const onSelect = (ids: string[]) => {
    setSelection(ids);
  };

  const renderCollectionMenu = () => {
    const actions = { onEdit, onShare, onManage };
    const menuItems = getCollectionMenu({ actions });
    const menuProps = {
      anchorEl: menuRef,
      anchorOrigin: {
        vertical: "top",
        horizontal: "right",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      }
    };

    if (isMenuVisible && menuRef) {
      return (
        <PopOverMenu
          className="CollectionPopOverMenu"
          title="Collection Options"
          menuProps={menuProps}
          isOpen={true}
          onClose={() => setMenuVisible(false)}
          menuItems={menuItems}
        />
      );
    }
  };

  const getRemoveCollectionText = () => (
    <p>Are you sure want to delete your <b>{collection?.folderName}</b> collection?</p>
  );

  const getRemoveGifsText = () => (
    <p>Are you sure want to delete <b>{selection.length}</b> items from your <b>{collection?.folderName}</b> collection?</p>
  );

  if (query.isError) {
    return (<div className="wide page collectionItemsPage">
      <p>Error loading collection.</p>
    </div>);
  }

  return (
    <Fragment>
      <div className="wide page collectionItemsPage">
        <CollectionHeader
          collection={collection}
          backLink={routes.userCollections(userName)}
          editable={isEditable}
          manage={isManage}
          more={handleMore}
          trash={onDeleteGifs}
        />

        {isManage && <div className="description">Please select the gifs you want to delete, then click the trash can buton.</div>}

        {!isManage && <div className="description">
          {collection?.description ?? "This collection has no description."}
        </div>}

        {query.isSuccess && userName && collectionId && <CollectionFeed
          user={userName}
          collection={collectionId}
          showPrivate={isEditable}
          manage={isManage}
          onSelect={onSelect}
        />}

        {renderCollectionMenu()}

        {collection && <EditCollectionPopup
          isOpen={isEditVisible}
          togglePopup={onClose}
          handleDeleteCollection={onDeleteCollection}
          collection={collection}
        />}

        <DeleteCollectionPopup
          isOpen={isDeleteCollectionVisible}
          togglePopup={onClose}
          handleDelete={onDeleteCollectionConfirm}
          text={getRemoveCollectionText()}
        />

        <DeleteCollectionPopup
          isOpen={isDeleteGifsVisible}
          togglePopup={onClose}
          handleDelete={onTrash}
          text={getRemoveGifsText()}
        />

        {menuRef && collection && isShareVisible && <ShareCollectionMenu
          anchorEl={menuRef}
          userName={userName}
          collectionId={collectionId}
          collectionName={collection.folderName}
          onClose={onClose}
        />}
      </div>

      <FooterAds/>
    </Fragment>
  );
};
